import { Injectable, Output } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { MachinePerformanceApiService } from '../api/machine-performance-api.service';
import { DefaultMachinePerformanceListDto } from '../../models/machine-performances/default-machine-performance-list-dto';
import { DefaultMachinePerformanceListFilter } from '../../models/machine-performances/default-machine-performance-list-filter';
import { GetProductionTechnicianAreaEnum, ProductionTechnicianArea } from '../../enums/production-technician-area';
import { MachinePerformanceType, MachinePerformanceTypeLabels } from '../../models/machine-performances/machine-performance-type';
import { MachineType, MachineTypeLabels } from '../../enums/machine-type';
import { MachineSize, getMachineSizeFromLabel } from '../../models/machines/machine-size';

@Injectable({
  providedIn: 'root'
})
export class MachinePerformanceListService {

  private dataSubject = new BehaviorSubject<DefaultMachinePerformanceListDto[] | null>(null);
  data$ = this.dataSubject;
  private currentMachineType: MachineType = MachineType.Forwarder;

  constructor(private machinePerformanceApiService: MachinePerformanceApiService) {
  }

  @Output() filterChange = new BehaviorSubject<DefaultMachinePerformanceListFilter>({
    search: [],
    type: [],
    area: [],
    size: [],
  });

  async loadMachinePerformances(machineType: MachineType) {
    this.currentMachineType = machineType;
    this.machinePerformanceApiService.getDefaultMachinePerformanceList(machineType).subscribe(data => {
      this.dataSubject.next(data);
    });
  }

  addMachinePerformance(element: DefaultMachinePerformanceListDto, machineTypeToReload: MachineType): Observable<void> {
    const payload = this.mapMachinePerformance(element);
    return this.machinePerformanceApiService.addDefaultMachinePerformance(payload).pipe(
      map(() => {
        this.loadMachinePerformances(machineTypeToReload);
        return undefined;
      })
    );
  }

  updateMachinePerformance(element: DefaultMachinePerformanceListDto) {

    this.machinePerformanceApiService.updateDefaultMachinePerformance(this.mapMachinePerformance(element)).subscribe(data => {
      console.log(data)
    });
  }

  deleteMachinePerformance(element: DefaultMachinePerformanceListDto) {
    const payload = this.mapMachinePerformance(element);
    this.machinePerformanceApiService.deleteDefaultMachinePerformance(payload).subscribe(() => {
      this.loadMachinePerformances(this.currentMachineType);
    });
  }

  applyFilter(filterValues: DefaultMachinePerformanceListFilter | undefined) {
    filterValues ? this.filterChange.next(filterValues) : console.log('no filter found');
  }

  mapMachinePerformance(element: DefaultMachinePerformanceListDto): DefaultMachinePerformanceListDto {
    const type = typeof element.type === 'string'
      ? this.getMachinePerformanceTypeFromLabel(element.type)
      : element.type;

    const machineType = typeof element.machineType === 'string'
      ? this.getMachineTypeFromLabel(element.machineType)
      : element.machineType;

    const area = typeof element.area === 'string'
      ? GetProductionTechnicianAreaEnum(element.area)
      : element.area;

    const size = typeof element.size === 'string'
      ? getMachineSizeFromLabel(element.size) ?? MachineSize.Small
      : element.size;

    return {
      ...element,
      type,
      machineType,
      area,
      size
    };
  }

  private getMachinePerformanceTypeFromLabel(label: string): MachinePerformanceType {
    const entry = Object.entries(MachinePerformanceTypeLabels).find(([, value]) => value === label);
    return entry ? Number(entry[0]) as MachinePerformanceType : MachinePerformanceType.Regeneration;
  }

  private getMachineTypeFromLabel(label: string): MachineType {
    const entry = Object.entries(MachineTypeLabels).find(([, value]) => value === label);
    return entry ? Number(entry[0]) as MachineType : MachineType.Forwarder;
  }
}
